import { injectable } from 'inversify';
import type {
  IAuthRepository,
  LoginRequest,
  LoginResponse,
  Enable2FAResponse,
} from '@/domain/repositories/IAuthRepository';
import { User } from '@/domain/entities/User';
import { InvalidCredentialsError } from '@/domain/errors/InvalidCredentialsError';

@injectable()
export class MockAuthRepository implements IAuthRepository {
  // Usuario hardcodeado para pruebas
  private readonly mockUser = {
    id: 1,
    username: 'admin',
    email: 'admin@coofit.com',
    password: 'admin123', // En producción esto NO debe estar aquí
    role: 'admin' as const,
    tenant: {
      id: 1,
      nombre: 'Clínica Demo',
      emailAdministrador: 'admin@coofit.com', // Email válido para el tenant
    },
  };

  async login(request: LoginRequest): Promise<LoginResponse> {
    // Simular delay de red
    await new Promise(resolve => setTimeout(resolve, 500));

    // Validar credenciales primero
    const credentialsValid = 
      request.username === this.mockUser.username &&
      request.password === this.mockUser.password;

    if (!credentialsValid) {
      throw new InvalidCredentialsError('Invalid username or password');
    }

    // Si las credenciales son válidas y se proporcionó código 2FA, validarlo
    if (request.twoFactorCode) {
      // Limpiar el código (eliminar espacios y caracteres no numéricos)
      const cleanCode = request.twoFactorCode.trim().replace(/\s/g, '');
      
      console.log('Validating 2FA code:', { 
        original: request.twoFactorCode, 
        cleaned: cleanCode, 
        length: cleanCode.length,
        isValid: /^\d{6}$/.test(cleanCode)
      });
      
      // Código 2FA válido: cualquier código de exactamente 6 dígitos
      if (cleanCode.length === 6 && /^\d{6}$/.test(cleanCode)) {
        try {
          const user = User.create(
            this.mockUser.id,
            this.mockUser.username,
            this.mockUser.email,
            this.mockUser.role,
            this.mockUser.tenant,
            new Date(),
            new Date()
          );

          // Generar token mock (en producción sería un JWT real)
          const mockToken = `mock_jwt_token_${Date.now()}`;

          return {
            token: mockToken,
            user,
            requiresTwoFactor: false,
            requiresTwoFactorSetup: false,
            expiresIn: 3600, // 1 hora
          };
        } catch (userError) {
          console.error('Error creating user:', userError);
          throw new InvalidCredentialsError('Error al crear usuario: ' + (userError instanceof Error ? userError.message : 'Unknown error'));
        }
      } else {
        throw new InvalidCredentialsError(`Código 2FA inválido. Recibido: "${request.twoFactorCode}" (limpiado: "${cleanCode}", longitud: ${cleanCode.length}). Debe tener exactamente 6 dígitos numéricos.`);
      }
    }

    // Si requiere 2FA pero no se proporcionó código
    if (request.username === 'admin' && !request.twoFactorCode) {
      // Simular que requiere 2FA
      return {
        token: null,
        user: null,
        requiresTwoFactor: true,
        requiresTwoFactorSetup: false,
      };
    }

    // Login exitoso sin 2FA (para otros usuarios que no sean admin)
    // En este caso, como solo tenemos admin, esto no debería ejecutarse
    // pero lo dejamos por si se agregan más usuarios en el futuro
    const user = User.create(
      this.mockUser.id,
      this.mockUser.username,
      this.mockUser.email,
      this.mockUser.role,
      this.mockUser.tenant,
      new Date(),
      new Date()
    );

    const mockToken = `mock_jwt_token_${Date.now()}`;

    return {
      token: mockToken,
      user,
      requiresTwoFactor: false,
      requiresTwoFactorSetup: false,
      expiresIn: 3600,
    };
  }

  async enable2FA(): Promise<Enable2FAResponse> {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Generar secret y QR Code URL mock
    const secret = 'MOCK_SECRET_KEY_FOR_2FA';
    const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=otpauth://totp/Coofit2:${this.mockUser.username}?secret=${secret}&issuer=Coofit2`;

    return {
      secret,
      qrCodeUrl,
    };
  }

  async verify2FASetup(code: string, secret: string): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Validar código (cualquier código de 6 dígitos es válido en mock)
    if (!code || code.length !== 6 || !/^\d+$/.test(code)) {
      throw new InvalidCredentialsError('Invalid verification code');
    }
    
    // En producción aquí se validaría el código TOTP real
  }

  async disable2FA(password: string): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    if (password !== this.mockUser.password) {
      throw new InvalidCredentialsError('Invalid password');
    }
  }

  async getCurrentUser(): Promise<User | null> {
    await new Promise(resolve => setTimeout(resolve, 100));
    
    const storedUser = localStorage.getItem('admin_user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        return User.create(
          userData.id,
          userData.username,
          userData.email,
          userData.role,
          userData.tenant || null,
          new Date(userData.createdAt),
          userData.lastLogin ? new Date(userData.lastLogin) : undefined
        );
      } catch (error) {
        return null;
      }
    }
    
    return null;
  }

  async changeUserTenant(tenantId: number | string): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Actualizar usuario en localStorage
    const storedUser = localStorage.getItem('admin_user');
    if (storedUser) {
      const userData = JSON.parse(storedUser);
      userData.tenant = {
        id: tenantId,
        nombre: `Clínica ${tenantId}`,
      };
      localStorage.setItem('admin_user', JSON.stringify(userData));
    }
  }
}
